/**
 * Known web crawlers by user agent. Checkout refuses them (checkoutWithStripe),
 * and the browser's PostHog drops their events. Meta's crawler accepts the
 * cookie banner and clicks the buy buttons, which created Stripe sessions,
 * Meta CAPI and PostHog checkout events (Oct 2026).
 *
 * Explicitly named crawlers only: a false positive refuses a real buyer. There
 * is no generic "bot", "crawler" or "spider" rule: real Android phones and
 * tablets are called «M bot 54», «POWER BOT» (Logicom) and «Spider 8»
 * (OSCAL). Apps people use (Pinterest, LinkedIn, Teams, Outlook, WhatsApp,
 * Telegram, Slack) are never on the list; only their companies' crawlers are
 * (Pinterestbot, LinkedInBot). An empty user agent is allowed: some privacy
 * browsers strip it. Failure modes: tests/checkout-crawler-guard/failure-modes.md.
 */

/**
 * Crawler names, matched case-insensitively as whole tokens: the characters
 * around a name may not be letters, digits, "_" or "-", so "Googlebot/2.1"
 * matches and "Applebot-Extended" (a robots.txt name) does not. Each name is
 * also safe as a plain substring, so posthog-js's custom_blocked_useragents
 * takes the list too.
 */
export const CRAWLER_USER_AGENT_TOKENS = [
  "meta-externalagent",
  "facebookexternalhit",
  "facebot",
  "headlesschrome",
  "googlebot",
  "adsbot-google",
  "google-inspectiontool",
  "bingbot",
  "ahrefsbot",
  "baiduspider",
  "yandexbot",
  "applebot",
  "amazonbot",
  "gptbot",
  "claudebot",
  "perplexitybot",
  "bytespider",
  "pinterestbot",
  "linkedinbot",
  "twitterbot",
  "discordbot",
] as const;

const TOKEN_CHARACTER = /[a-z0-9_-]/;

function hasWholeToken(lowerUserAgent: string, token: string): boolean {
  for (
    let index = lowerUserAgent.indexOf(token);
    index !== -1;
    index = lowerUserAgent.indexOf(token, index + 1)
  ) {
    const before = lowerUserAgent[index - 1] ?? "";
    const after = lowerUserAgent[index + token.length] ?? "";
    if (!TOKEN_CHARACTER.test(before) && !TOKEN_CHARACTER.test(after)) {
      return true;
    }
  }
  return false;
}

/** The matched crawler name (from the list, never the user agent), or null. */
export function matchCrawlerUserAgent(
  userAgent: string | null | undefined,
): string | null {
  if (!userAgent?.trim()) return null;
  const lowerUserAgent = userAgent.toLowerCase();
  return (
    CRAWLER_USER_AGENT_TOKENS.find((token) =>
      hasWholeToken(lowerUserAgent, token),
    ) ?? null
  );
}
